import React, { useEffect, useState } from "react";
import { Container, Typography, Box, Paper, CircularProgress } from "@mui/material";
import PublicLayout from "../components/PublicLayout";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";

const PADEL_EAGLE_PRIVACY_URL = "https://www.padeleagle.de/datenschutz";

interface PrivacySection {
  title: string;
  content: string[];
}

const PrivacyPolicy: React.FC = () => {
  const [sections, setSections] = useState<PrivacySection[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentOrg } = useOrganization();
  const isPadelEagle =
    currentOrg?.slug === "padel-eagle" || /padeleagle/i.test(window.location.hostname);

  useEffect(() => {
    // Padel Eagle uses its own privacy policy page on the main website
    if (isPadelEagle) {
      window.location.replace(PADEL_EAGLE_PRIVACY_URL);
      return;
    }
    fetchPrivacyPolicy();
  }, [isPadelEagle]);

  const fetchPrivacyPolicy = async () => {
    try {
      const response = await apiRequest("/api/public/privacy-policy");
      if (response.ok) {
        const data = await response.json();
        setSections(data.sections || []);
      }
    } catch (err) {
      console.error("Error fetching privacy policy:", err);
      // Set default on error
      setSections([
        {
          title: "Privacy Policy",
          content: [
            "This privacy policy explains how we collect, use, and protect your personal information.",
          ],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (loading || isPadelEagle) {
    return (
      <PublicLayout>
        <Container maxWidth="md" sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Container>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          {sections.map((section, index) => (
            <Box key={index} sx={{ mb: index < sections.length - 1 ? 4 : 0 }}>
              <Typography
                variant={index === 0 ? "h3" : "h5"}
                gutterBottom
                fontWeight="bold"
                color={index === 0 ? "inherit" : "primary"}
              >
                {section.title}
              </Typography>
              {section.content.map((paragraph, pIndex) => (
                <Typography key={pIndex} variant="body1" paragraph>
                  {paragraph}
                </Typography>
              ))}
            </Box>
          ))}
        </Paper>
      </Container>
    </PublicLayout>
  );
};

export default PrivacyPolicy;
